/**
 * Indian mobile numbers only, in every field a shopper types a phone number
 * into: sign-in, checkout, the profile address form, Edit Profile and the
 * account dialog.
 *
 * Each field shows a fixed +91 beside it and holds only the 10-digit national
 * number. The full number (+91XXXXXXXXXX) is what the profile app and Shopify
 * receive, so surfaces convert with canonical() on the way out and national()
 * on the way in.
 *
 * A field opts in with data-pf-phone-india. This file keeps it digits-only and
 * at most 10 digits as it is typed, pasted or autofilled, and keeps its native
 * validity in step so checkValidity()/reportValidity() give the same messages.
 * When a keystroke or paste is rejected it fires `pf:phone-india-message` on
 * the field; a surface that shows its own error calls preventDefault(),
 * otherwise the browser's validation bubble shows the message.
 *
 * A named form can carry the full number with a hidden
 * <input data-pf-phone-canonical> beside the field; it is kept in step here.
 *
 * Messages come from snippets/pf-indian-phone.liquid.
 */
(function () {
  "use strict";
  if (window.PFIndianPhone) return;

  var DIAL = "+91";
  var SELECTOR = "[data-pf-phone-india]";
  var FALLBACK = {
    required: "Enter a phone number.",
    digits: "Use digits only. Letters, spaces and symbols are not allowed.",
    length: "Enter all 10 digits of the mobile number.",
    start: "Indian mobile numbers start with 6, 7, 8 or 9.",
  };
  var cachedMessages;

  function messages() {
    if (cachedMessages) return cachedMessages;
    var node = document.querySelector("[data-pf-phone-india-strings]");
    var loaded = {};
    try {
      loaded = node ? JSON.parse(node.textContent) : {};
    } catch (error) {}
    cachedMessages = {};
    Object.keys(FALLBACK).forEach(function (key) {
      cachedMessages[key] = loaded[key] || FALLBACK[key];
    });
    return cachedMessages;
  }

  // Whatever was pasted or autofilled ("+91 98765 43210", "09876543210"),
  // reduced to the national digits.
  function digitsOf(value) {
    var digits = String(value || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.indexOf("91") === 0) return digits.slice(2);
    if (digits.length === 11 && digits.charAt(0) === "0") return digits.slice(1);
    return digits;
  }

  // A stored number ("+919876543210") as the field shows it ("9876543210").
  // A number from another country is left whole, so the field flags it
  // instead of silently turning it into a different Indian number.
  function national(value) {
    var cleaned = String(value || "").replace(/[\s\-().]/g, "");
    if (cleaned.indexOf(DIAL) === 0) return cleaned.slice(DIAL.length);
    if (/^91[6-9]\d{9}$/.test(cleaned)) return cleaned.slice(2);
    return cleaned;
  }

  function canonical(value) {
    var typed = String(value || "").trim();
    return typed ? DIAL + typed : "";
  }

  function error(value, options) {
    var typed = String(value || "").trim();
    var text = messages();
    if (!typed) return options && options.required === false ? "" : text.required;
    if (/\D/.test(typed)) return text.digits;
    if (typed.length !== 10) return text.length;
    if (!/^[6-9]/.test(typed)) return text.start;
    return "";
  }

  function sync(input) {
    input.setCustomValidity(error(input.value, { required: input.required }));
    var hidden = input.parentElement && input.parentElement.querySelector("[data-pf-phone-canonical]");
    if (hidden) hidden.value = canonical(input.value);
  }

  function fill(input, value) {
    if (!input) return;
    input.value = national(value);
    sync(input);
  }

  function announce(input, message) {
    var event = new CustomEvent("pf:phone-india-message", {
      bubbles: true,
      cancelable: true,
      detail: { message: message },
    });
    if (input.dispatchEvent(event)) {
      input.setCustomValidity(message);
      input.reportValidity();
    }
  }

  document.addEventListener("beforeinput", function (event) {
    var input = event.target.closest && event.target.closest(SELECTOR);
    if (!input || !event.data) return;
    if (event.inputType === "insertFromPaste" || event.inputType === "insertFromDrop") return;
    if (/\D/.test(event.data)) {
      event.preventDefault();
      announce(input, messages().digits);
    }
  });

  document.addEventListener("paste", function (event) {
    var input = event.target.closest && event.target.closest(SELECTOR);
    if (!input) return;
    var text = event.clipboardData ? event.clipboardData.getData("text") : "";
    event.preventDefault();
    var start = input.selectionStart == null ? input.value.length : input.selectionStart;
    var end = input.selectionEnd == null ? input.value.length : input.selectionEnd;
    var next = digitsOf(input.value.slice(0, start) + text + input.value.slice(end)).slice(0, 10);
    input.value = next;
    input.setSelectionRange(next.length, next.length);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    if (/[^\d\s+\-().]/.test(text)) announce(input, messages().digits);
  });

  // "change" too: restored drafts (assets/pf-checkout.js) set the value and
  // announce it with a change event rather than an input event.
  function sanitise(event) {
    var input = event.target.closest && event.target.closest(SELECTOR);
    if (!input) return;
    var clean = digitsOf(input.value).slice(0, 10);
    if (clean !== input.value) input.value = clean;
    sync(input);
  }
  document.addEventListener("input", sanitise, true);
  document.addEventListener("change", sanitise, true);

  // Fields rendered with a stored +91 number, or filled by the profile hydrate
  // pass, show just the national digits.
  function normaliseAll() {
    document.querySelectorAll(SELECTOR).forEach(function (input) {
      if (input.value) fill(input, input.value);
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", normaliseAll, { once: true });
  } else {
    normaliseAll();
  }
  document.addEventListener("pf:profile:hydrated", normaliseAll);

  window.PFIndianPhone = {
    DIAL: DIAL,
    national: national,
    canonical: canonical,
    error: error,
    fill: fill,
  };
})();
